import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { baseURL } from "../utils/common";

const SOLUTION_TAGS_URL = `${baseURL}/content/admin/tags/`;
const SOLUTION_TAG_CREATE_URL = `${baseURL}/content/admin/tag/create/`;
const SOLUTION_TAG_UPDATE_URL = (id) => `${baseURL}/content/admin/tag/${id}/update/`;
const SOLUTION_TAG_DELETE_URL = (id) => `${baseURL}/content/admin/tag/${id}/delete/`;

const SOLUTIONS_URL = `${baseURL}/content/admin/`;
const SOLUTION_CREATE_URL = `${baseURL}/content/admin/solution/create/`;
const SOLUTION_DETAILS_URL = (id) => `${baseURL}/content/admin/solution/${id}/details/`;
const SOLUTION_UPDATE_URL = (id) => `${baseURL}/content/admin/solution/${id}/update/`;
const SOLUTION_DELETE_URL = (id) => `${baseURL}/content/admin/solution/${id}/delete/`;

const SOLUTION_GALLERY_CREATE_URL = (id) =>
    `${baseURL}/content/admin/solution/${id}/gallery/create/`;

const SOLUTION_GALLERY_UPDATE_URL = (id) =>
    `${baseURL}/content/admin/gallery/${id}/update/`;

const SOLUTION_GALLERY_DELETE_URL = (id) =>
    `${baseURL}/content/admin/gallery/${id}/delete/`;

const LANGS = [
    { key: "ru", label: "RU" },
    { key: "en", label: "EN" },
    { key: "kk", label: "KK" },
];

const emptyLang = () => ({
    ru: "",
    en: "",
    kk: "",
});

const safeLang = (value) => ({
    ru: value?.ru || "",
    en: value?.en || "",
    kk: value?.kk || "",
});

const boolToString = (value) => (value ? "true" : "false");

const getText = (value) => {
    if (!value) return "—";
    if (typeof value === "string") return value || "—";

    return value.ru || value.en || value.kk || "—";
};

const getFileName = (file) => {
    if (!file) return "";
    return file.name || "";
};

const parseIdsText = (value) => {
    if (!value) return [];

    return value
        .split(",")
        .map((item) => Number(String(item).trim()))
        .filter((item) => item > 0);
};

const formatIdsText = (items) => {
    return (items || []).map((item) => item.id).join(", ");
};

const clearMultipartHeaders = (headers) => {
    if (!headers) return;

    if (typeof headers.delete === "function") {
        headers.delete("Content-Type");
        headers.delete("content-type");
    }

    delete headers["Content-Type"];
    delete headers["content-type"];

    if (headers.common) {
        delete headers.common["Content-Type"];
        delete headers.common["content-type"];
    }

    if (headers.post) {
        delete headers.post["Content-Type"];
        delete headers.post["content-type"];
    }
};

const uploadFormData = (url, formData) => {
    return axios.post(url, formData, {
        transformRequest: [
            (data, headers) => {
                clearMultipartHeaders(headers);
                return data;
            },
        ],
    });
};

const createEmptyTagForm = () => ({
    id: null,
    position: 0,
    slug: "",
    system_name: "",
    name: emptyLang(),
    short_description: emptyLang(),
    image: null,
    clear_image: false,
    is_active: true,
});

const createEmptySolutionForm = () => ({
    id: null,
    position: 0,
    slug: "",
    system_name: "",
    tag_id: "",
    title: emptyLang(),
    short_description: emptyLang(),
    text: emptyLang(),
    cover_image: null,
    mobile_cover_image: null,
    clear_cover_image: false,
    clear_mobile_cover_image: false,
    recommended_categories: [],
    recommended_products_text: "",
    seo_title: emptyLang(),
    seo_description: emptyLang(),
    seo_keywords: emptyLang(),
    is_main: false,
    is_active: true,
});

const createEmptyGalleryForm = () => ({
    position: 0,
    title: emptyLang(),
    short_description: emptyLang(),
    image: null,
    is_active: true,
});

const LangInputs = ({ label, value, onChange, textarea = false }) => {
    return (
        <div className="content-lang-field">
            <span>{label}</span>

            <div className="content-lang-grid">
                {LANGS.map((lang) => (
                    <label key={lang.key}>
                        <small>{lang.label}</small>

                        {textarea ? (
                            <textarea
                                value={value?.[lang.key] || ""}
                                onChange={(e) => onChange(lang.key, e.target.value)}
                            />
                        ) : (
                            <input
                                value={value?.[lang.key] || ""}
                                onChange={(e) => onChange(lang.key, e.target.value)}
                            />
                        )}
                    </label>
                ))}
            </div>
        </div>
    );
};

const Modal = ({ isOpen, title, description, onClose, children, wide = false }) => {
    if (!isOpen) return null;

    return (
        <div className="content-modal-overlay">
            <div className={`content-modal ${wide ? "wide" : ""}`}>
                <div className="content-modal-head">
                    <div>
                        <h2>{title}</h2>
                        {description && <p>{description}</p>}
                    </div>

                    <button type="button" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="content-modal-body">
                    {children}
                </div>
            </div>
        </div>
    );
};

export const ContentSolutionsPage = () => {
    const [tags, setTags] = useState([]);
    const [solutions, setSolutions] = useState([]);
    const [solutionFields, setSolutionFields] = useState(null);

    const [tagForm, setTagForm] = useState(createEmptyTagForm());
    const [solutionForm, setSolutionForm] = useState(createEmptySolutionForm());
    const [galleryForm, setGalleryForm] = useState(createEmptyGalleryForm());
    const [galleryDrafts, setGalleryDrafts] = useState([]);

    const [tagQuery, setTagQuery] = useState("");
    const [solutionQuery, setSolutionQuery] = useState("");
    const [solutionTagFilter, setSolutionTagFilter] = useState("");
    const [solutionActiveFilter, setSolutionActiveFilter] = useState("");

    const [isTagModalOpen, setIsTagModalOpen] = useState(false);
    const [isSolutionModalOpen, setIsSolutionModalOpen] = useState(false);

    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState("");
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const categoryOptions = useMemo(() => {
        return solutionFields?.recommended_categories?.options || [];
    }, [solutionFields]);

    const tagOptions = useMemo(() => {
        return tags.map((tag) => ({
            value: tag.id,
            label: tag.system_name,
        }));
    }, [tags]);

    const setTagLangValue = (field, lang, value) => {
        setTagForm((prev) => ({
            ...prev,
            [field]: {
                ...prev[field],
                [lang]: value,
            },
        }));
    };

    const setSolutionLangValue = (field, lang, value) => {
        setSolutionForm((prev) => ({
            ...prev,
            [field]: {
                ...prev[field],
                [lang]: value,
            },
        }));
    };

    const setGalleryLangValue = (field, lang, value) => {
        setGalleryForm((prev) => ({
            ...prev,
            [field]: {
                ...prev[field],
                [lang]: value,
            },
        }));
    };

    const setGalleryDraftLangValue = (galleryId, field, lang, value) => {
        setGalleryDrafts((prev) =>
            prev.map((item) => {
                if (item.id !== galleryId) return item;

                return {
                    ...item,
                    [field]: {
                        ...item[field],
                        [lang]: value,
                    },
                };
            })
        );
    };

    const loadTags = async () => {
        try {
            const response = await axios.get(SOLUTION_TAGS_URL, {
                params: {
                    limit: 100,
                    ordering: "position",
                    q: tagQuery,
                },
            });

            const data = response.data;

            if (data.success) {
                setTags(data.results || []);
            }
        } catch (e) {
            setError(e.response?.data?.message || "Не удалось загрузить теги");
        }
    };

    const loadSolutions = async () => {
        try {
            const response = await axios.get(SOLUTIONS_URL, {
                params: {
                    limit: 100,
                    ordering: "position",
                    q: solutionQuery,
                    tag_id: solutionTagFilter,
                    is_active: solutionActiveFilter,
                },
            });

            const data = response.data;

            if (data.success) {
                setSolutions(data.results || []);
            }
        } catch (e) {
            setError(e.response?.data?.message || "Не удалось загрузить решения");
        }
    };

    const loadSolutionCreateFields = async () => {
        try {
            const response = await axios.get(SOLUTION_CREATE_URL);
            const data = response.data;

            if (data.success) {
                setSolutionFields(data.fields || null);
            }
        } catch (e) {
            console.log("Не удалось загрузить поля решений", e);
        }
    };

    const resetTagForm = () => {
        setTagForm(createEmptyTagForm());
    };

    const resetSolutionForm = () => {
        setSolutionForm(createEmptySolutionForm());
        setGalleryDrafts([]);
        setGalleryForm(createEmptyGalleryForm());
    };

    const openCreateTagModal = () => {
        resetTagForm();
        setError("");
        setMessage("");
        setIsTagModalOpen(true);
    };

    const openEditTagModal = (tag) => {
        setTagForm({
            id: tag.id,
            position: tag.position || 0,
            slug: tag.slug || "",
            system_name: tag.system_name || "",
            name: safeLang(tag.name),
            short_description: safeLang(tag.short_description),
            image: null,
            clear_image: false,
            is_active: Boolean(tag.is_active),
        });

        setError("");
        setMessage("");
        setIsTagModalOpen(true);
    };

    const closeTagModal = () => {
        setIsTagModalOpen(false);
        resetTagForm();
    };

    const openCreateSolutionModal = () => {
        resetSolutionForm();
        setError("");
        setMessage("");
        setIsSolutionModalOpen(true);
    };

    const applySolutionToForm = (item) => {
        setSolutionForm({
            id: item.id,
            position: item.position || 0,
            slug: item.slug || "",
            system_name: item.system_name || "",
            tag_id: item.tag?.id || "",
            title: safeLang(item.title),
            short_description: safeLang(item.short_description),
            text: safeLang(item.text),
            cover_image: null,
            mobile_cover_image: null,
            clear_cover_image: false,
            clear_mobile_cover_image: false,
            recommended_categories: (item.recommended_categories || []).map((category) => category.id),
            recommended_products_text: formatIdsText(item.recommended_products || []),
            seo_title: safeLang(item.seo_title),
            seo_description: safeLang(item.seo_description),
            seo_keywords: safeLang(item.seo_keywords),
            is_main: Boolean(item.is_main),
            is_active: Boolean(item.is_active),
        });

        setGalleryDrafts(
            (item.gallery || []).map((galleryItem) => ({
                ...galleryItem,
                title: safeLang(galleryItem.title),
                short_description: safeLang(galleryItem.short_description),
                new_image: null,
                clear_image: false,
            }))
        );
    };

    const openEditSolutionModal = async (solutionId) => {
        if (savingKey) return;

        setSavingKey(`solution-load-${solutionId}`);
        setError("");
        setMessage("");

        try {
            const response = await axios.get(SOLUTION_DETAILS_URL(solutionId));
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось загрузить решение");
                return;
            }

            applySolutionToForm(data.item);
            setIsSolutionModalOpen(true);
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка загрузки решения");
        } finally {
            setSavingKey("");
        }
    };

    const closeSolutionModal = () => {
        setIsSolutionModalOpen(false);
        resetSolutionForm();
    };

    const saveTag = async () => {
        if (savingKey) return;

        if (!tagForm.slug.trim()) {
            setError("Укажите slug тега");
            return;
        }

        if (!tagForm.system_name.trim()) {
            setError("Укажите название тега для системы");
            return;
        }

        setSavingKey("tag");
        setError("");
        setMessage("");

        try {
            const formData = new FormData();

            formData.append("position", tagForm.position || 0);
            formData.append("slug", tagForm.slug.trim());
            formData.append("system_name", tagForm.system_name.trim());
            formData.append("name", JSON.stringify(tagForm.name || {}));
            formData.append("short_description", JSON.stringify(tagForm.short_description || {}));
            formData.append("is_active", boolToString(tagForm.is_active));

            if (tagForm.image) {
                formData.append("image", tagForm.image);
            }

            if (tagForm.clear_image) {
                formData.append("clear_image", "true");
            }

            const url = tagForm.id
                ? SOLUTION_TAG_UPDATE_URL(tagForm.id)
                : SOLUTION_TAG_CREATE_URL;

            const response = await uploadFormData(url, formData);
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось сохранить тег");
                return;
            }

            setMessage(data.message || "Тег сохранён");
            closeTagModal();
            await loadTags();
            await loadSolutions();
            await loadSolutionCreateFields();
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка сохранения тега");
        } finally {
            setSavingKey("");
        }
    };

    const deleteTag = async (tagId) => {
        if (savingKey) return;

        const isConfirmed = window.confirm("Удалить тег решения?");

        if (!isConfirmed) return;

        setSavingKey(`tag-delete-${tagId}`);
        setError("");
        setMessage("");

        try {
            const response = await axios.post(SOLUTION_TAG_DELETE_URL(tagId));
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось удалить тег");
                return;
            }

            setMessage(data.message || "Тег удалён");
            closeTagModal();
            await loadTags();
            await loadSolutions();
            await loadSolutionCreateFields();
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка удаления тега");
        } finally {
            setSavingKey("");
        }
    };

    const toggleCategory = (categoryId) => {
        const cleanId = Number(categoryId);

        setSolutionForm((prev) => {
            const exists = prev.recommended_categories.includes(cleanId);

            return {
                ...prev,
                recommended_categories: exists
                    ? prev.recommended_categories.filter((id) => id !== cleanId)
                    : [...prev.recommended_categories, cleanId],
            };
        });
    };

    const saveSolution = async () => {
        if (savingKey) return;

        if (!solutionForm.slug.trim()) {
            setError("Укажите slug решения");
            return;
        }

        if (!solutionForm.system_name.trim()) {
            setError("Укажите название решения для системы");
            return;
        }

        setSavingKey("solution");
        setError("");
        setMessage("");

        try {
            const formData = new FormData();

            formData.append("position", solutionForm.position || 0);
            formData.append("slug", solutionForm.slug.trim());
            formData.append("system_name", solutionForm.system_name.trim());
            formData.append("tag_id", solutionForm.tag_id || "");

            formData.append("title", JSON.stringify(solutionForm.title || {}));
            formData.append("short_description", JSON.stringify(solutionForm.short_description || {}));
            formData.append("text", JSON.stringify(solutionForm.text || {}));

            formData.append("recommended_categories", JSON.stringify(solutionForm.recommended_categories || []));
            formData.append("recommended_products", JSON.stringify(parseIdsText(solutionForm.recommended_products_text)));

            formData.append("seo_title", JSON.stringify(solutionForm.seo_title || {}));
            formData.append("seo_description", JSON.stringify(solutionForm.seo_description || {}));
            formData.append("seo_keywords", JSON.stringify(solutionForm.seo_keywords || {}));

            formData.append("is_main", boolToString(solutionForm.is_main));
            formData.append("is_active", boolToString(solutionForm.is_active));

            if (solutionForm.cover_image) {
                formData.append("cover_image", solutionForm.cover_image);
            }

            if (solutionForm.mobile_cover_image) {
                formData.append("mobile_cover_image", solutionForm.mobile_cover_image);
            }

            if (solutionForm.clear_cover_image) {
                formData.append("clear_cover_image", "true");
            }

            if (solutionForm.clear_mobile_cover_image) {
                formData.append("clear_mobile_cover_image", "true");
            }

            const url = solutionForm.id
                ? SOLUTION_UPDATE_URL(solutionForm.id)
                : SOLUTION_CREATE_URL;

            const response = await uploadFormData(url, formData);
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось сохранить решение");
                return;
            }

            setMessage(data.message || "Решение сохранено");
            applySolutionToForm(data.item);
            await loadSolutions();
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка сохранения решения");
        } finally {
            setSavingKey("");
        }
    };

    const deleteSolution = async (solutionId) => {
        if (savingKey) return;

        const isConfirmed = window.confirm("Удалить решение?");

        if (!isConfirmed) return;

        setSavingKey(`solution-delete-${solutionId}`);
        setError("");
        setMessage("");

        try {
            const response = await axios.post(SOLUTION_DELETE_URL(solutionId));
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось удалить решение");
                return;
            }

            setMessage(data.message || "Решение удалено");
            closeSolutionModal();
            await loadSolutions();
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка удаления решения");
        } finally {
            setSavingKey("");
        }
    };

    const createGalleryImage = async () => {
        if (savingKey) return;

        if (!solutionForm.id) {
            setError("Сначала сохраните решение");
            return;
        }

        if (!galleryForm.image) {
            setError("Прикрепите картинку галереи");
            return;
        }

        setSavingKey("gallery-create");
        setError("");
        setMessage("");

        try {
            const formData = new FormData();

            formData.append("position", galleryForm.position || 0);
            formData.append("title", JSON.stringify(galleryForm.title || {}));
            formData.append("short_description", JSON.stringify(galleryForm.short_description || {}));
            formData.append("is_active", boolToString(galleryForm.is_active));
            formData.append("image", galleryForm.image);

            const response = await uploadFormData(SOLUTION_GALLERY_CREATE_URL(solutionForm.id), formData);
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось добавить картинку");
                return;
            }

            setMessage(data.message || "Картинка добавлена");
            setGalleryForm(createEmptyGalleryForm());
            applySolutionToForm(data.solution);
            await loadSolutions();
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка добавления картинки");
        } finally {
            setSavingKey("");
        }
    };

    const updateGalleryDraft = (galleryId, field, value) => {
        setGalleryDrafts((prev) =>
            prev.map((item) => {
                if (item.id !== galleryId) return item;

                return {
                    ...item,
                    [field]: value,
                };
            })
        );
    };

    const saveGalleryImage = async (item) => {
        if (savingKey) return;

        setSavingKey(`gallery-${item.id}`);
        setError("");
        setMessage("");

        try {
            const formData = new FormData();

            formData.append("position", item.position || 0);
            formData.append("title", JSON.stringify(item.title || {}));
            formData.append("short_description", JSON.stringify(item.short_description || {}));
            formData.append("is_active", boolToString(item.is_active));

            if (item.new_image) {
                formData.append("image", item.new_image);
            }

            if (item.clear_image) {
                formData.append("clear_image", "true");
            }

            const response = await uploadFormData(SOLUTION_GALLERY_UPDATE_URL(item.id), formData);
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось сохранить картинку");
                return;
            }

            setMessage(data.message || "Картинка сохранена");
            applySolutionToForm(data.solution);
            await loadSolutions();
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка сохранения картинки");
        } finally {
            setSavingKey("");
        }
    };

    const deleteGalleryImage = async (galleryId) => {
        if (savingKey) return;

        const isConfirmed = window.confirm("Удалить картинку из галереи?");

        if (!isConfirmed) return;

        setSavingKey(`gallery-delete-${galleryId}`);
        setError("");
        setMessage("");

        try {
            const response = await axios.post(SOLUTION_GALLERY_DELETE_URL(galleryId));
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось удалить картинку");
                return;
            }

            setMessage(data.message || "Картинка удалена");
            applySolutionToForm(data.solution);
            await loadSolutions();
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка удаления картинки");
        } finally {
            setSavingKey("");
        }
    };

    const loadInitialData = async () => {
        setLoading(true);
        setError("");

        await Promise.all([
            loadTags(),
            loadSolutions(),
            loadSolutionCreateFields(),
        ]);

        setLoading(false);
    };

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            loadTags();
        }, 350);

        return () => clearTimeout(timer);
    }, [tagQuery]);

    useEffect(() => {
        const timer = setTimeout(() => {
            loadSolutions();
        }, 350);

        return () => clearTimeout(timer);
    }, [solutionQuery, solutionTagFilter, solutionActiveFilter]);

    if (loading) {
        return (
            <section className="content-solutions-page">
                <div className="content-loading">Загрузка контента...</div>
            </section>
        );
    }

    return (
        <section className="content-solutions-page">
            <div className="content-page-head">
                <div>
                    <h1>Решения</h1>
                    <p>Теги, карточки решений, статья, галерея и рекомендованные товары</p>
                </div>

                <div className="content-head-actions">
                    <button type="button" onClick={openCreateTagModal}>
                        + Новый тег
                    </button>

                    <button type="button" onClick={openCreateSolutionModal}>
                        + Новое решение
                    </button>
                </div>
            </div>

            {error && <div className="content-error">{error}</div>}
            {message && <div className="content-success">{message}</div>}

            <div className="content-clean-layout">
                <aside className="content-panel">
                    <div className="content-panel-head">
                        <div>
                            <h2>Теги</h2>
                            <p>Фильтры решений</p>
                        </div>
                    </div>

                    <input
                        className="content-search"
                        value={tagQuery}
                        onChange={(e) => setTagQuery(e.target.value)}
                        placeholder="Поиск тега"
                    />

                    <div className="content-tag-list">
                        {tags.map((tag) => (
                            <div className="content-tag-card" key={tag.id}>
                                <button type="button" onClick={() => openEditTagModal(tag)}>
                                    <strong>{tag.system_name}</strong>
                                    <span>{tag.slug}</span>
                                </button>

                                <button
                                    className="content-mini-danger"
                                    type="button"
                                    onClick={() => deleteTag(tag.id)}
                                >
                                    ×
                                </button>
                            </div>
                        ))}

                        {tags.length === 0 && (
                            <div className="content-empty">Теги не найдены</div>
                        )}
                    </div>
                </aside>

                <main className="content-panel">
                    <div className="content-panel-head">
                        <div>
                            <h2>Список решений</h2>
                            <p>Создание и редактирование открывается в модалке</p>
                        </div>
                    </div>

                    <div className="content-solution-filters">
                        <input
                            value={solutionQuery}
                            onChange={(e) => setSolutionQuery(e.target.value)}
                            placeholder="Поиск по названию или slug"
                        />

                        <select
                            value={solutionTagFilter}
                            onChange={(e) => setSolutionTagFilter(e.target.value)}
                        >
                            <option value="">Все теги</option>
                            {tagOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>

                        <select
                            value={solutionActiveFilter}
                            onChange={(e) => setSolutionActiveFilter(e.target.value)}
                        >
                            <option value="">Все</option>
                            <option value="true">Активные</option>
                            <option value="false">Неактивные</option>
                        </select>
                    </div>

                    <div className="content-solutions-list">
                        {solutions.map((solution) => (
                            <div className="content-solution-card" key={solution.id}>
                                <button type="button" onClick={() => openEditSolutionModal(solution.id)}>
                                    <div className="content-solution-img">
                                        {solution.cover_image && solution.cover_image !== "—" ? (
                                            <img src={solution.cover_image} alt="" />
                                        ) : (
                                            <span>IMG</span>
                                        )}
                                    </div>

                                    <div>
                                        <strong>{solution.system_name}</strong>
                                        <p>{solution.slug}</p>
                                        <small>
                                            {solution.tag?.system_name || "Без тега"} · галерея: {solution.gallery_count || 0}
                                        </small>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    className="content-mini-danger"
                                    onClick={() => deleteSolution(solution.id)}
                                >
                                    ×
                                </button>
                            </div>
                        ))}

                        {solutions.length === 0 && (
                            <div className="content-empty">Решения не найдены</div>
                        )}
                    </div>
                </main>
            </div>

            <Modal
                isOpen={isTagModalOpen}
                title={tagForm.id ? "Редактировать тег" : "Новый тег"}
                description="Тег нужен для фильтрации решений на фронте"
                onClose={closeTagModal}
            >
                <div className="content-form-grid one">
                    <label>
                        <span>Позиция</span>
                        <input
                            type="number"
                            value={tagForm.position}
                            onChange={(e) => setTagForm((prev) => ({ ...prev, position: e.target.value }))}
                        />
                    </label>

                    <label>
                        <span>Slug</span>
                        <input
                            value={tagForm.slug}
                            onChange={(e) => setTagForm((prev) => ({ ...prev, slug: e.target.value }))}
                        />
                    </label>

                    <label>
                        <span>Название для системы</span>
                        <input
                            value={tagForm.system_name}
                            onChange={(e) => setTagForm((prev) => ({ ...prev, system_name: e.target.value }))}
                        />
                    </label>
                </div>

                <LangInputs
                    label="Название"
                    value={tagForm.name}
                    onChange={(lang, value) => setTagLangValue("name", lang, value)}
                />

                <LangInputs
                    label="Краткое описание"
                    value={tagForm.short_description}
                    onChange={(lang, value) => setTagLangValue("short_description", lang, value)}
                    textarea
                />

                <div className="content-file-line">
                    <label>
                        <span>Картинка / иконка</span>
                        <input
                            type="file"
                            onChange={(e) =>
                                setTagForm((prev) => ({
                                    ...prev,
                                    image: e.target.files?.[0] || null,
                                }))
                            }
                        />
                        {tagForm.image && <small>{getFileName(tagForm.image)}</small>}
                    </label>

                    {tagForm.id && (
                        <label className="content-check">
                            <input
                                type="checkbox"
                                checked={tagForm.clear_image}
                                onChange={(e) =>
                                    setTagForm((prev) => ({
                                        ...prev,
                                        clear_image: e.target.checked,
                                    }))
                                }
                            />
                            <span>Удалить текущую картинку</span>
                        </label>
                    )}

                    <label className="content-check">
                        <input
                            type="checkbox"
                            checked={tagForm.is_active}
                            onChange={(e) =>
                                setTagForm((prev) => ({
                                    ...prev,
                                    is_active: e.target.checked,
                                }))
                            }
                        />
                        <span>Активен</span>
                    </label>
                </div>

                <div className="content-actions">
                    <button type="button" onClick={saveTag} disabled={savingKey === "tag"}>
                        {savingKey === "tag" ? "Сохраняем..." : "Сохранить тег"}
                    </button>

                    {tagForm.id && (
                        <button type="button" className="danger" onClick={() => deleteTag(tagForm.id)}>
                            Удалить
                        </button>
                    )}

                    <button type="button" className="ghost" onClick={closeTagModal}>
                        Закрыть
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={isSolutionModalOpen}
                title={solutionForm.id ? "Редактировать решение" : "Новое решение"}
                description="Карточка, статья, SEO, товары и галерея решения"
                onClose={closeSolutionModal}
                wide
            >
                <div className="content-form-grid">
                    <label>
                        <span>Позиция</span>
                        <input
                            type="number"
                            value={solutionForm.position}
                            onChange={(e) =>
                                setSolutionForm((prev) => ({ ...prev, position: e.target.value }))
                            }
                        />
                    </label>

                    <label>
                        <span>Slug</span>
                        <input
                            value={solutionForm.slug}
                            onChange={(e) =>
                                setSolutionForm((prev) => ({ ...prev, slug: e.target.value }))
                            }
                        />
                    </label>

                    <label>
                        <span>Название для системы</span>
                        <input
                            value={solutionForm.system_name}
                            onChange={(e) =>
                                setSolutionForm((prev) => ({ ...prev, system_name: e.target.value }))
                            }
                        />
                    </label>

                    <label>
                        <span>Тег</span>
                        <select
                            value={solutionForm.tag_id}
                            onChange={(e) =>
                                setSolutionForm((prev) => ({ ...prev, tag_id: e.target.value }))
                            }
                        >
                            <option value="">Без тега</option>
                            {tagOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                <LangInputs
                    label="Заголовок"
                    value={solutionForm.title}
                    onChange={(lang, value) => setSolutionLangValue("title", lang, value)}
                />

                <LangInputs
                    label="Краткое описание"
                    value={solutionForm.short_description}
                    onChange={(lang, value) => setSolutionLangValue("short_description", lang, value)}
                    textarea
                />

                <LangInputs
                    label="Текст статьи"
                    value={solutionForm.text}
                    onChange={(lang, value) => setSolutionLangValue("text", lang, value)}
                    textarea
                />

                <div className="content-file-grid">
                    <label>
                        <span>Главная картинка</span>
                        <input
                            type="file"
                            onChange={(e) =>
                                setSolutionForm((prev) => ({
                                    ...prev,
                                    cover_image: e.target.files?.[0] || null,
                                }))
                            }
                        />
                        {solutionForm.cover_image && <small>{getFileName(solutionForm.cover_image)}</small>}
                    </label>

                    <label>
                        <span>Мобильная картинка</span>
                        <input
                            type="file"
                            onChange={(e) =>
                                setSolutionForm((prev) => ({
                                    ...prev,
                                    mobile_cover_image: e.target.files?.[0] || null,
                                }))
                            }
                        />
                        {solutionForm.mobile_cover_image && (
                            <small>{getFileName(solutionForm.mobile_cover_image)}</small>
                        )}
                    </label>

                    {solutionForm.id && (
                        <>
                            <label className="content-check">
                                <input
                                    type="checkbox"
                                    checked={solutionForm.clear_cover_image}
                                    onChange={(e) =>
                                        setSolutionForm((prev) => ({
                                            ...prev,
                                            clear_cover_image: e.target.checked,
                                        }))
                                    }
                                />
                                <span>Удалить главную</span>
                            </label>

                            <label className="content-check">
                                <input
                                    type="checkbox"
                                    checked={solutionForm.clear_mobile_cover_image}
                                    onChange={(e) =>
                                        setSolutionForm((prev) => ({
                                            ...prev,
                                            clear_mobile_cover_image: e.target.checked,
                                        }))
                                    }
                                />
                                <span>Удалить мобильную</span>
                            </label>
                        </>
                    )}
                </div>

                <div className="content-recommendations">
                    <div>
                        <h3>Рекомендованные категории</h3>
                        <p>Будут показаны ниже статьи решения</p>

                        <div className="content-category-list">
                            {categoryOptions.map((option) => (
                                <button
                                    type="button"
                                    key={option.value}
                                    className={
                                        solutionForm.recommended_categories.includes(Number(option.value))
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() => toggleCategory(option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}

                            {categoryOptions.length === 0 && (
                                <span>Категории не найдены</span>
                            )}
                        </div>
                    </div>

                    <label>
                        <span>Рекомендованные товары</span>
                        <textarea
                            value={solutionForm.recommended_products_text}
                            onChange={(e) =>
                                setSolutionForm((prev) => ({
                                    ...prev,
                                    recommended_products_text: e.target.value,
                                }))
                            }
                            placeholder="ID товаров через запятую: 1, 2, 3"
                        />
                        <small>Товары можно искать в разделе товаров, сюда вставлять ID.</small>
                    </label>
                </div>

                <div className="content-seo-box">
                    <h3>SEO</h3>

                    <LangInputs
                        label="SEO title"
                        value={solutionForm.seo_title}
                        onChange={(lang, value) => setSolutionLangValue("seo_title", lang, value)}
                    />

                    <LangInputs
                        label="SEO description"
                        value={solutionForm.seo_description}
                        onChange={(lang, value) => setSolutionLangValue("seo_description", lang, value)}
                        textarea
                    />

                    <LangInputs
                        label="SEO keywords"
                        value={solutionForm.seo_keywords}
                        onChange={(lang, value) => setSolutionLangValue("seo_keywords", lang, value)}
                    />
                </div>

                <div className="content-toggle-row">
                    <label className="content-check">
                        <input
                            type="checkbox"
                            checked={solutionForm.is_main}
                            onChange={(e) =>
                                setSolutionForm((prev) => ({ ...prev, is_main: e.target.checked }))
                            }
                        />
                        <span>Показывать на главной</span>
                    </label>

                    <label className="content-check">
                        <input
                            type="checkbox"
                            checked={solutionForm.is_active}
                            onChange={(e) =>
                                setSolutionForm((prev) => ({ ...prev, is_active: e.target.checked }))
                            }
                        />
                        <span>Активно</span>
                    </label>
                </div>

                <div className="content-actions sticky">
                    <button type="button" onClick={saveSolution} disabled={savingKey === "solution"}>
                        {savingKey === "solution" ? "Сохраняем..." : "Сохранить решение"}
                    </button>

                    {solutionForm.id && (
                        <button type="button" className="danger" onClick={() => deleteSolution(solutionForm.id)}>
                            Удалить
                        </button>
                    )}

                    <button type="button" className="ghost" onClick={closeSolutionModal}>
                        Закрыть
                    </button>
                </div>

                {solutionForm.id && (
                    <div className="content-modal-section">
                        <div className="content-panel-head">
                            <div>
                                <h2>Галерея решения</h2>
                                <p>Дополнительные картинки внутри страницы решения</p>
                            </div>
                        </div>

                        <div className="content-gallery-create">
                            <div className="content-form-grid">
                                <label>
                                    <span>Позиция</span>
                                    <input
                                        type="number"
                                        value={galleryForm.position}
                                        onChange={(e) =>
                                            setGalleryForm((prev) => ({
                                                ...prev,
                                                position: e.target.value,
                                            }))
                                        }
                                    />
                                </label>

                                <label>
                                    <span>Картинка</span>
                                    <input
                                        type="file"
                                        onChange={(e) =>
                                            setGalleryForm((prev) => ({
                                                ...prev,
                                                image: e.target.files?.[0] || null,
                                            }))
                                        }
                                    />
                                </label>
                            </div>

                            <LangInputs
                                label="Название картинки"
                                value={galleryForm.title}
                                onChange={(lang, value) => setGalleryLangValue("title", lang, value)}
                            />

                            <LangInputs
                                label="Описание картинки"
                                value={galleryForm.short_description}
                                onChange={(lang, value) => setGalleryLangValue("short_description", lang, value)}
                                textarea
                            />

                            <label className="content-check">
                                <input
                                    type="checkbox"
                                    checked={galleryForm.is_active}
                                    onChange={(e) =>
                                        setGalleryForm((prev) => ({
                                            ...prev,
                                            is_active: e.target.checked,
                                        }))
                                    }
                                />
                                <span>Активна</span>
                            </label>

                            <div className="content-actions">
                                <button
                                    type="button"
                                    onClick={createGalleryImage}
                                    disabled={savingKey === "gallery-create"}
                                >
                                    {savingKey === "gallery-create" ? "Добавляем..." : "Добавить картинку"}
                                </button>
                            </div>
                        </div>

                        <div className="content-gallery-list">
                            {galleryDrafts.map((item) => (
                                <div className="content-gallery-card" key={item.id}>
                                    <div className="content-gallery-preview">
                                        {item.image && item.image !== "—" ? (
                                            <img src={item.image} alt="" />
                                        ) : (
                                            <span>IMG</span>
                                        )}
                                    </div>

                                    <div className="content-gallery-body">
                                        <div className="content-form-grid">
                                            <label>
                                                <span>Позиция</span>
                                                <input
                                                    type="number"
                                                    value={item.position}
                                                    onChange={(e) =>
                                                        updateGalleryDraft(item.id, "position", e.target.value)
                                                    }
                                                />
                                            </label>

                                            <label>
                                                <span>Заменить картинку</span>
                                                <input
                                                    type="file"
                                                    onChange={(e) =>
                                                        updateGalleryDraft(
                                                            item.id,
                                                            "new_image",
                                                            e.target.files?.[0] || null
                                                        )
                                                    }
                                                />
                                            </label>
                                        </div>

                                        <LangInputs
                                            label="Название"
                                            value={item.title}
                                            onChange={(lang, value) =>
                                                setGalleryDraftLangValue(item.id, "title", lang, value)
                                            }
                                        />

                                        <LangInputs
                                            label="Описание"
                                            value={item.short_description}
                                            onChange={(lang, value) =>
                                                setGalleryDraftLangValue(item.id, "short_description", lang, value)
                                            }
                                            textarea
                                        />

                                        <div className="content-toggle-row">
                                            <label className="content-check">
                                                <input
                                                    type="checkbox"
                                                    checked={item.is_active}
                                                    onChange={(e) =>
                                                        updateGalleryDraft(item.id, "is_active", e.target.checked)
                                                    }
                                                />
                                                <span>Активна</span>
                                            </label>

                                            <label className="content-check">
                                                <input
                                                    type="checkbox"
                                                    checked={item.clear_image}
                                                    onChange={(e) =>
                                                        updateGalleryDraft(item.id, "clear_image", e.target.checked)
                                                    }
                                                />
                                                <span>Удалить картинку</span>
                                            </label>
                                        </div>

                                        <div className="content-actions">
                                            <button
                                                type="button"
                                                onClick={() => saveGalleryImage(item)}
                                                disabled={savingKey === `gallery-${item.id}`}
                                            >
                                                {savingKey === `gallery-${item.id}` ? "Сохраняем..." : "Сохранить"}
                                            </button>

                                            <button
                                                type="button"
                                                className="danger"
                                                onClick={() => deleteGalleryImage(item.id)}
                                            >
                                                Удалить
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {galleryDrafts.length === 0 && (
                                <div className="content-empty">Галерея пока пустая</div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </section>
    );
};
